import React, { useState, useRef, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import { User, Heart, Mail, Lock, Image, ArrowRight, Eye, EyeOff } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Cloudinary configuration
const cloudinaryConfig = {
  cloudName: 'dryrxwnej',
  uploadPreset: 'chat-uploads',
  apiUrl: 'https://api.cloudinary.com/v1_1/dryrxwnej/image/upload'
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Helper function to update user offline status
const updateUserOfflineStatus = async (userId) => {
  try {
    await setDoc(doc(db, "users", userId), {
      isOnline: false,
      lastSeen: serverTimestamp()
    }, { merge: true });
    console.log('✅ User status updated to offline');
  } catch (error) {
    console.error('❌ Error updating offline status:', error);
  }
};

// Username generator utility - updated to generate lowercase word/name + numbers
const generateUsername = () => {
  // Common names and words (all lowercase)
  const words = [
    'alex', 'sam', 'jade', 'river', 'sage', 'sky', 'ocean', 'forest',
    'moon', 'star', 'sun', 'rain', 'snow', 'fire', 'earth', 'wind',
    'blue', 'red', 'green', 'gold', 'silver', 'rose', 'lily', 'iris',
    'cat', 'dog', 'bird', 'fish', 'bear', 'wolf', 'lion', 'eagle',
    'swift', 'calm', 'wild', 'free', 'pure', 'bright', 'dark', 'light',
    'nova', 'luna', 'aria', 'echo', 'zara', 'kira', 'mira', 'vera'
  ];
  
  const randomWord = words[Math.floor(Math.random() * words.length)];
  
  // Generate random numbers to fill the remaining characters
  const remainingLength = 10 - randomWord.length;
  let numbers = '';
  
  for (let i = 0; i < remainingLength; i++) {
    numbers += Math.floor(Math.random() * 10).toString();
  }
  
  return randomWord + numbers;
};

// Check if username exists in database
const checkUsernameExists = async (username) => {
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("username", "==", username));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error("Error checking username:", error);
    return false;
  }
};

// Generate unique username
const generateUniqueUsername = async () => {
  let username;
  let attempts = 0;
  const maxAttempts = 10;

  do {
    username = generateUsername();
    attempts++;
    
    if (attempts >= maxAttempts) {
      // If we can't find a unique username after 10 attempts, add timestamp
      const timestamp = Date.now().toString().slice(-3);
      // Choose a short word to leave room for timestamp
      const shortWords = ['cat', 'dog', 'sun', 'sky', 'red', 'blue'];
      const shortWord = shortWords[Math.floor(Math.random() * shortWords.length)];
      username = shortWord + timestamp;
      break;
    }
  } while (await checkUsernameExists(username));

  return username;
};

// Enhanced Session Management Class with Auto-Logout Feature
class SessionManager {
  constructor() {
    this.activityTimeout = null;
    this.sessionCheckInterval = null;
    this.warningTimeout = null;
    this.SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
    this.WARNING_TIME = 2 * 60 * 1000; // Show warning 2 minutes before timeout
    this.isInitialized = false;
    this.isDestroyed = false;
    this.activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    this.lastActivityTime = Date.now();
    this.boundResetTimer = this.resetInactivityTimer.bind(this);
    this.boundVisibilityChange = this.handleVisibilityChange.bind(this);
    this.boundBeforeUnload = this.handleBeforeUnload.bind(this);
    this.warningShown = false;
    
    // Console log initialization
    console.log('🔐 SessionManager initialized with 10-minute auto-logout feature');
    console.log('📊 Session timeout set to:', this.SESSION_TIMEOUT / (60 * 1000), 'minutes');
  }

  showInactivityNotification = () => {
    if (this.warningShown || this.isDestroyed) return;
    
    this.warningShown = true;
    console.log('⚠️ Showing inactivity warning to user');
    
    const userResponse = window.confirm(
      'You will be automatically logged out in 2 minutes due to inactivity. Click OK to stay logged in.'
    );
    
    if (userResponse) {
      console.log('✅ User chose to stay logged in - resetting timer');
      this.resetInactivityTimer();
      this.warningShown = false;
    } else {
      console.log('❌ User dismissed warning - will be logged out soon');
    }
  };

  updateLastActivity = async () => {
    const now = Date.now();
    this.lastActivityTime = now;
    
    console.log('🔄 Activity detected at:', new Date(now).toLocaleTimeString());
    
    if (auth.currentUser) {
      try {
        const userSessionRef = doc(db, 'userSessions', auth.currentUser.uid);
        await setDoc(userSessionRef, {
          lastActivity: now,
          userId: auth.currentUser.uid,
          timestamp: serverTimestamp()
        }, { merge: true });
        
        console.log('💾 Last activity updated in Firestore for user:', auth.currentUser.uid);
      } catch (error) {
        console.error('❌ Error updating last activity in Firestore:', error);
      }
    }
  };

  logoutUser = async () => {
    if (this.isDestroyed) return;

    console.log('🚪 Initiating automatic logout due to 10 minutes of inactivity');
    
    try {
      if (auth.currentUser) {
        console.log('📝 Marking session as expired for user:', auth.currentUser.uid);
        
        // Mark session as expired in Firestore
        const userSessionRef = doc(db, 'userSessions', auth.currentUser.uid);
        await setDoc(userSessionRef, {
          lastActivity: 0,
          loggedOutAt: Date.now(),
          reason: 'inactivity_timeout'
        }, { merge: true });
        
        // Update user status to offline BEFORE signing out
        await updateUserOfflineStatus(auth.currentUser.uid);
      }
      
      this.cleanup();
      await signOut(auth);
      
      console.log('🔓 User successfully signed out due to inactivity');
      
      // Show notification to user
      alert('You have been logged out due to inactivity (10 minutes).');
      console.log('📢 Inactivity logout notification displayed to user');
      
      // Redirect to signup page
      if (window.location.pathname !== '/signup') {
        console.log('🔀 Redirecting to signup page');
        window.location.href = '/signup';
      }
      
    } catch (error) {
      console.error('❌ Error during automatic logout:', error);
    }
  };

  resetInactivityTimer = () => {
    if (this.isDestroyed) return;

    this.warningShown = false;
    
    // Clear existing timeouts
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
      console.log('🧹 Cleared existing logout timeout');
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      console.log('🧹 Cleared existing warning timeout');
    }
    
    // Update last activity
    this.updateLastActivity();
    
    // Calculate timeout times
    const warningTime = this.SESSION_TIMEOUT - this.WARNING_TIME; // 8 minutes
    const logoutTime = this.SESSION_TIMEOUT; // 10 minutes
    
    console.log('⏰ Setting new timers:');
    console.log('  - Warning in:', warningTime / (60 * 1000), 'minutes');
    console.log('  - Logout in:', logoutTime / (60 * 1000), 'minutes');
    
    // Set warning timeout (8 minutes)
    this.warningTimeout = setTimeout(() => {
      console.log('⚠️ 8 minutes of inactivity reached - showing warning');
      this.showInactivityNotification();
    }, warningTime);
    
    // Set logout timeout (10 minutes)
    this.activityTimeout = setTimeout(() => {
      console.log('⏰ 10 minutes of inactivity reached - logging out user');
      this.logoutUser();
    }, logoutTime);
  };

  checkSessionValidity = async () => {
    if (this.isDestroyed || !auth.currentUser) return;

    try {
      const now = Date.now();
      const localTimeDiff = now - this.lastActivityTime;
      
      console.log('🔍 Checking session validity:');
      console.log('  - Local inactivity time:', Math.round(localTimeDiff / (60 * 1000)), 'minutes');
      
      if (localTimeDiff > this.SESSION_TIMEOUT) {
        console.log('❌ Local session expired - logging out');
        await this.logoutUser();
        return;
      }

      // Check Firestore session data
      const userSessionRef = doc(db, 'userSessions', auth.currentUser.uid);
      const sessionDoc = await getDoc(userSessionRef);
      
      if (sessionDoc.exists()) {
        const data = sessionDoc.data();
        const firestoreLastActivity = data.lastActivity;
        const firestoreTimeDiff = now - firestoreLastActivity;
        
        console.log('  - Firestore inactivity time:', Math.round(firestoreTimeDiff / (60 * 1000)), 'minutes');
        
        if (firestoreLastActivity === 0 || firestoreTimeDiff > this.SESSION_TIMEOUT) {
          console.log('❌ Firestore session expired - logging out');
          await this.logoutUser();
          return;
        }
        
        // Sync local time with Firestore if there's a significant difference
        if (Math.abs(this.lastActivityTime - firestoreLastActivity) > 30000) {
          console.log('🔄 Syncing local activity time with Firestore');
          this.lastActivityTime = firestoreLastActivity;
        }
      } else {
        console.log('📝 No session document found - creating new one');
        await this.updateLastActivity();
      }
      
      console.log('✅ Session is valid');
    } catch (error) {
      console.error('❌ Error checking session validity:', error);
    }
  };

  handleVisibilityChange = () => {
    if (this.isDestroyed) return;
    
    if (document.visibilityState === 'visible' && auth.currentUser) {
      console.log('👁️ Page became visible - checking session and resetting timer');
      this.checkSessionValidity();
      this.resetInactivityTimer();
    } else if (document.visibilityState === 'hidden') {
      console.log('🙈 Page became hidden');
    }
  };

  handleBeforeUnload = async () => {
    console.log('🚪 Page unloading - updating user status to offline');
    // Update user status to offline when page is unloading
    if (auth.currentUser) {
      try {
        await updateUserOfflineStatus(auth.currentUser.uid);
      } catch (error) {
        console.error('❌ Error updating offline status on page unload:', error);
      }
    }
    this.cleanup();
  };

  initialize = () => {
    if (this.isInitialized || this.isDestroyed) return;

    this.isInitialized = true;
    this.isDestroyed = false;
    
    console.log('🚀 Initializing SessionManager with activity tracking');
    console.log('👂 Listening for activity events:', this.activityEvents);
    
    // Add activity event listeners
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.boundResetTimer, true);
    });

    // Check session validity every 30 seconds
    this.sessionCheckInterval = setInterval(() => {
      console.log('⏱️ Performing periodic session validity check');
      this.checkSessionValidity();
    }, 30 * 1000);
    
    // Initialize the timer
    this.resetInactivityTimer();

    // Handle page visibility change
    document.addEventListener('visibilitychange', this.boundVisibilityChange);
    
    // Handle page unload
    window.addEventListener('beforeunload', this.boundBeforeUnload);
    
    console.log('✅ SessionManager fully initialized and active');
  };

  cleanup = () => {
    if (!this.isInitialized || this.isDestroyed) return;

    console.log('🧹 Cleaning up SessionManager');
    
    this.isDestroyed = true;
    this.isInitialized = false;
    
    // Clear timeouts and intervals
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
      this.activityTimeout = null;
      console.log('🧹 Cleared activity timeout');
    }
    
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
      console.log('🧹 Cleared warning timeout');
    }
    
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
      console.log('🧹 Cleared session check interval');
    }
    
    // Remove event listeners
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.boundResetTimer, true);
    });

    document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    window.removeEventListener('beforeunload', this.boundBeforeUnload);
    
    console.log('✅ SessionManager cleanup completed');
  };

  // Method to get current session status (for debugging)
  getSessionStatus = () => {
    const now = Date.now();
    const inactiveTime = now - this.lastActivityTime;
    const remainingTime = this.SESSION_TIMEOUT - inactiveTime;
    
    return {
      isActive: !this.isDestroyed && this.isInitialized,
      lastActivity: new Date(this.lastActivityTime).toLocaleString(),
      inactiveTime: Math.round(inactiveTime / (60 * 1000)),
      remainingTime: Math.round(remainingTime / (60 * 1000)),
      warningShown: this.warningShown
    };
  };
}

// Create a single instance of SessionManager
const sessionManager = new SessionManager();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.sessionManager = sessionManager;
  
  // Add debug function to console
  window.getSessionStatus = () => {
    const status = sessionManager.getSessionStatus();
    console.log('📊 Current Session Status:', status);
    return status;
  };
  
  console.log('🔧 Debug: Use window.getSessionStatus() to check current session status');
}

const SignUp = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    gender: '',
  });
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [generatedUsername, setGeneratedUsername] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Check if user is already logged in and setup session management
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        console.log('👤 User authentication state changed - user logged in:', user.uid);
        
        // Always cleanup first to prevent multiple instances
        sessionManager.cleanup();
        
        await sessionManager.updateLastActivity();
        sessionManager.initialize();
        
        console.log('🔐 Auto-logout session management activated for user');
        
        // Update user online status
        try {
          // Use the uploaded profile picture as the display picture
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.profilePic && user.photoURL !== userData.profilePic) {
              await updateProfile(user, {
                photoURL: userData.profilePic
              });
            }
          }
          
          await setDoc(doc(db, "users", user.uid), {
            isOnline: true,
            lastSeen: serverTimestamp()
          }, { merge: true });
          
          console.log('✅ User online status updated');
        } catch (error) {
          console.error('❌ Error updating online status:', error);
        }
        
        // Redirect to specific chat room instead of general chat
        navigate('/chat/nhm1gYlqiLR3Wfo5rGssZx7dRhI3');
      } else {
        console.log('👤 User logged out - cleaning up session management');
        sessionManager.cleanup();
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, [navigate]);

  // Generate username when switching to sign up
  useEffect(() => {
    if (isSignUp && !generatedUsername) {
      generateUniqueUsername().then(username => {
        setGeneratedUsername(username);
      });
    }
  }, [isSignUp, generatedUsername]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (!auth.currentUser) {
        console.log('🧹 Component unmounting - cleaning up session manager');
        sessionManager.cleanup();
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      
      // File size validation (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Profile picture must be less than 5MB');
        return;
      }
      
      setProfileImage(file);
      
      // Create image preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    setError('');
    
    if (isSignUp) {
      // Sign Up validation
      if (!formData.email.trim()) {
        setError('Email is required');
        return false;
      }
      
      if (!formData.password.trim()) {
        setError('Password is required');
        return false;
      }
      
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return false;
      }
      
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
      
      if (!formData.gender) {
        setError('Please select your gender');
        return false;
      }
      
      if (!profileImage) {
        setError('Profile picture is required');
        return false;
      }
    } else {
      // Sign In validation
      if (!formData.email.trim()) {
        setError('Email is required');
        return false;
      }
      
      if (!formData.password.trim()) {
        setError('Password is required');
        return false;
      }
    }
    
    return true;
  };

  const uploadToCloudinary = async (file, userId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', cloudinaryConfig.uploadPreset);
    formData.append('folder', `dating-profiles/${userId}`);
    
    try {
      const xhr = new XMLHttpRequest();
      
      // Set up progress tracking
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
        }
      };
      
      // Return a promise for the upload
      return new Promise((resolve, reject) => {
        xhr.open('POST', cloudinaryConfig.apiUrl, true);
        
        xhr.onload = function() {
          if (xhr.status === 200) {
            const response = JSON.parse(xhr.responseText);
            resolve(response.secure_url);
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error during upload'));
        };
        
        xhr.send(formData);
      });
    } catch (error) {
      console.error('Error uploading to Cloudinary:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      if (isSignUp) {
        // Handle Sign Up
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );
        
        const user = userCredential.user;
        
        // Start with 0% progress
        setUploadProgress(0);
        
        // Upload profile picture to Cloudinary
        const profilePicUrl = await uploadToCloudinary(profileImage, user.uid);
        
        // Update user profile with generated username and photo URL
        await updateProfile(user, {
          displayName: generatedUsername,
          photoURL: profilePicUrl
        });
        
        try {
          // Save additional user data to Firestore
          await setDoc(doc(db, "users", user.uid), {
            uid: user.uid,
            username: generatedUsername,
            email: formData.email,
            gender: formData.gender,
            profilePic: profilePicUrl,
            isOnline: true,
            lastSeen: serverTimestamp(),
            createdAt: serverTimestamp()
          });
          
         
          await setDoc(doc(db, "profiles", user.uid), {
            firstName: generatedUsername,
            lastName: "",
            email: formData.email,
            gender: formData.gender === 'male' ? 'Male' : formData.gender === 'female' ? 'Female' : 'Other',
            photoUrl: profilePicUrl,
            createdAt: serverTimestamp()
          });
        } catch (firestoreError) {
          console.error("Firestore error:", firestoreError);
          setError(`Account created but profile data couldn't be saved: ${firestoreError.message}`);
          setTimeout(() => navigate('/chat/nhm1gYlqiLR3Wfo5rGssZx7dRhI3'), 3000);
        }
      } else {
        // Handle Sign In
        await signInWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );
        navigate('/chat/nhm1gYlqiLR3Wfo5rGssZx7dRhI3');
      }
    } catch (error) {
      console.error("Auth error details:", error);
      
      // Handle specific error codes
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('Email is already in use');
          break;
        case 'auth/invalid-email':
          setError('Invalid email format');
          break;
        case 'auth/weak-password':
          setError('Password is too weak');
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          setError('Incorrect email or password');
          break;
        case 'auth/invalid-credential':
          setError('Incorrect email or password');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later.');
          break;
        default:
          setError(`Error: ${error.message || 'An unknown error occurred'}`);
      }
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const toggleAuthMode = () => {
    // Clear form data when switching between sign-up and sign-in
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      gender: '',
    });
    setProfileImage(null);
    setImagePreview(null);
    setError('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setGeneratedUsername('');
    setIsSignUp(!isSignUp);
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50 to-stone-100 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl flex rounded-2xl shadow-2xl overflow-hidden bg-white/95 backdrop-blur-sm border border-stone-200">
          {/* Left side - Image and branding */}
          <div className="hidden md:block w-1/2 bg-gradient-to-br from-stone-800 via-stone-700 to-amber-800 text-white p-12 flex flex-col justify-between relative overflow-hidden">
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-black/10 to-transparent"></div>
            <div className="relative z-10">
              <h1 className="text-4xl font-bold mb-6 flex items-center text-stone-50">
                <Heart className="mr-3 text-amber-300" size={36} />
                VineMatched
              </h1>
              <p className="text-lg mb-8 text-stone-100 leading-relaxed">
              This is a safe, anonymous space where you can share your struggles, receive emotional support, and connect with caring listeners who understand your journey.
              </p>
            </div>
            
            <div className="relative z-10">
              <h3 className="text-xl font-semibold mb-4 text-stone-100">Why VineMatched?</h3>
              <ul className="space-y-3">
                <li className="flex items-center text-stone-200">
                  <ArrowRight size={16} className="mr-3 text-amber-300 flex-shrink-0" />
                  Annonymus
                </li>
                <li className="flex items-center text-stone-200">
                  <ArrowRight size={16} className="mr-3 text-amber-300 flex-shrink-0" />
                  Safe
                </li>
                <li className="flex items-center text-stone-200">
                  <ArrowRight size={16} className="mr-3 text-amber-300 flex-shrink-0" />
                  Compassionate
                </li>
              </ul>
            </div>
          </div>
          
          {/* Right side - Auth form */}
          <div className="w-full md:w-1/2 p-8 bg-gradient-to-b from-white to-stone-50">
            <h2 className="text-3xl font-bold text-stone-800 mb-6 text-center">
              {isSignUp ? 'Create an Account' : 'Welcome Back'}
            </h2>
            
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {isSignUp && generatedUsername && (
                <div className="mb-4">
                  <label className="block text-stone-700 text-sm font-semibold mb-2 flex items-center">
                    <User size={16} className="mr-2 text-stone-600" />
                    Your Generated Username
                  </label>
                  <div className="w-full p-3 border border-stone-300 rounded-lg bg-stone-50 text-stone-700 font-mono text-center">
                    {generatedUsername}
                  </div>
                  <p className="text-xs text-stone-500 mt-1">
                    This unique username has been automatically generated for you
                  </p>
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-stone-700 text-sm font-semibold mb-2 flex items-center">
                  <Mail size={16} className="mr-2 text-stone-600" />
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-white text-stone-900"
                  placeholder="Your email address"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-stone-700 text-sm font-semibold mb-2 flex items-center">
                  <Lock size={16} className="mr-2 text-stone-600" />
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-white pr-10 text-stone-900"
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-500 hover:text-stone-700 focus:outline-none transition-colors duration-200"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              {/* Password Recovery Link */}
              {!isSignUp && (
                <div className="mb-4 text-right">
                  <Link 
                    to="/recover" 
                    className="text-sm text-amber-700 hover:text-amber-800 hover:underline transition-colors duration-200"
                  >
                    Forgot your password?
                  </Link>
                </div>
              )}
              
              {isSignUp && (
                <div className="mb-4">
                  <label className="block text-stone-700 text-sm font-semibold mb-2 flex items-center">
                    <Lock size={16} className="mr-2 text-stone-600" />
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-white pr-10 text-stone-900"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-500 hover:text-stone-700 focus:outline-none transition-colors duration-200"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              )}
              
              {isSignUp && (
                <div className="mb-4">
                  <label className="block text-stone-700 text-sm font-semibold mb-2">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full p-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-white text-stone-900"
                  >
                    <option value="">Select your gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              )}
              
              {isSignUp && (
                <div className="mb-6">
                  <label className="block text-stone-700 text-sm font-semibold mb-2 flex items-center">
                    <Image size={16} className="mr-2 text-stone-600" />
                    Profile Picture
                  </label>
                  <div className="flex items-center space-x-4">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      className="bg-stone-100 hover:bg-stone-200 text-stone-700 px-4 py-2 rounded-lg transition-all duration-200 flex items-center border border-stone-300"
                    >
                      <Image size={16} className="mr-2" />
                      Choose Photo
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      accept="image/*"
                      className="hidden"
                    />
                    {imagePreview && (
                      <img
                        src={imagePreview}
                        alt="Profile preview"
                        className="w-16 h-16 rounded-full object-cover border-2 border-stone-300 shadow-sm"
                      />
                    )}
                  </div>
                  {profileImage && (
                    <p className="text-sm text-green-700 mt-2">
                      ✓ {profileImage.name} selected
                    </p>
                  )}
                </div>
              )}
              
              {/* Upload Progress */}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="mb-4">
                  <div className="bg-stone-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-amber-500 to-amber-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-stone-600 mt-1">
                    Uploading... {uploadProgress}%
                  </p>
                </div>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className={`w-full p-3 rounded-lg text-white font-semibold transition-all duration-200 ${
                  loading
                    ? 'bg-stone-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-stone-700 via-amber-700 to-stone-800 hover:from-stone-800 hover:via-amber-800 hover:to-stone-900 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                }`}
              >
                {loading 
                  ? (isSignUp ? 'Creating Account...' : 'Signing In...') 
                  : (isSignUp ? 'Create Account' : 'Sign In')
                }
              </button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-stone-600">
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                <button
                  onClick={toggleAuthMode}
                  className="ml-2 text-amber-700 hover:text-amber-800 font-semibold hover:underline transition-colors duration-200"
                >
                  {isSignUp ? 'Sign In' : 'Sign Up'}
                </button>
              </p>
            </div>
            
            {/* Terms and Privacy - Show only on Sign Up */}
            {isSignUp && (
              <div className="mt-4 text-center">
                <p className="text-xs text-stone-500 leading-relaxed">
                  By creating an account, you agree to our{' '}
                  <Link to="/termsofservice" className="text-amber-700 hover:underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacypolicy" className="text-amber-700 hover:underline">
                    Privacy Policy
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SignUp;